// client/src/pages/Admin/ExamEditor/index.jsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import customFetch from '../../api/customFetch';
import ExamQuestionEditor from "./ExamQuestionEditor.jsx";

// רשימת הסקטורים - צריך להיות זהה לשרת
const SECTORS = [
  "אחמש",
  "גבסן",
  "הנהלה",
  "טכאי אקו",
  "טכנאי רנטגן",
  "מאמנים",
  "מזכירות",
  "מעבדה",
  "רופאים",
  "סיעוד",
];

export default function ExamEditor() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState({
    slug: "",
    title: "",
    description: "",
    passScore: 70,
    timeLimit: null, // בדקות, null = אין הגבלה
    maxAttempts: 3,
    targetSectors: [],
    supervisorEmail: "",
    sendReportOnCompletion: true,
    questions: [],
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isNew = slug === "new";

  useEffect(() => {
    console.log("ExamEditor useParams:", { slug, isNew });

    // אם זה מבחן חדש, לא לטעון מהשרת
    if (isNew) {
      console.log("New exam - skipping fetch");
      return;
    }

    // אם אין slug או שהוא לא תקין, לא לטעון
    if (!slug || slug === "undefined") {
      console.log("Invalid slug - skipping fetch");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await customFetch.get(`/exams/admin/${slug}`);
        console.log("Exam data received:", data);

        if (data.success && data.exam) {
          setExam({
            ...data.exam,
            targetSectors: data.exam.targetSectors || [],
          });
        } else {
          setError("מבנה נתונים לא תקין");
        }
      } catch (e) {
        console.error("Error loading exam:", e);
        if (e.response?.status === 401 || e.response?.status === 403) {
          nav("/admin/login");
        } else {
          setError("שגיאה בטעינת המבחן");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, nav]);

  const save = async () => {
    // ולידציה בסיסית
    if (!exam.slug) {
      setError("נדרש מזהה למבחן");
      return;
    }
    if (!exam.title) {
      setError("נדרש שם למבחן");
      return;
    }
    if (exam.targetSectors.length === 0) {
      setError("נדרש לבחור לפחות סקטור אחד");
      return;
    }
    if (exam.questions.length === 0) {
      setError("נדרש להוסיף לפחות שאלה אחת");
      return;
    }
    if (exam.passScore < 0 || exam.passScore > 100) {
      setError("ציון עובר חייב להיות בין 0 ל-100");
      return;
    }

    // ולידציה של שאלות
    for (let i = 0; i < exam.questions.length; i++) {
      const q = exam.questions[i];
      if (!q.question) {
        setError(`שאלה ${i + 1}: נדרש טקסט שאלה`);
        return;
      }
      if (q.options.length !== 4) {
        setError(`שאלה ${i + 1}: נדרשות בדיוק 4 תשובות`);
        return;
      }
      if (!q.correctAnswer) {
        setError(`שאלה ${i + 1}: נדרש לבחור תשובה נכונה`);
        return;
      }
      const hasEmptyOption = q.options.some(opt => !opt.text.trim());
      if (hasEmptyOption) {
        setError(`שאלה ${i + 1}: כל התשובות חייבות להכיל טקסט`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const endpoint = isNew ? "/exams" : `/exams/${exam.slug}`;
      const method = isNew ? "post" : "put";
      
      const { data } = await customFetch[method](endpoint, exam);
      console.log("Save response:", data);

      if (data.success && data.exam) {
        setExam(data.exam);
        if (isNew) nav(`/admin/exams/${data.exam.slug}`);
      } else {
        setError("שגיאה בשמירה - מבנה תשובה לא תקין");
      }
    } catch (e) {
      console.error("Save error:", e);
      setError(`שגיאה בשמירת המבחן: ${e.response?.data?.message || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async () => {
    try {
      setLoading(true);
      const newStatus = !exam.isActive;
      
      const { data } = await customFetch.patch(`/exams/${exam.slug}/status`, {
        isActive: newStatus
      });

      if (data.success && data.exam) {
        setExam(data.exam);
      } else {
        setExam(prev => ({ ...prev, isActive: newStatus }));
      }
    } catch (e) {
      console.error("Toggle status error:", e);
      setError("שגיאה בשינוי סטטוס המבחן");
    } finally {
      setLoading(false);
    }
  };

  const importJson = async (file) => {
    try {
      const txt = await file.text();
      const obj = JSON.parse(txt);
      setExam({
        ...obj,
        targetSectors: obj.targetSectors || [],
      });
      setError(null);
    } catch (e) {
      console.error("Import error:", e);
      setError("שגיאה בייבוא קובץ JSON");
    }
  };

  const exportJson = () => {
    const dataStr = JSON.stringify(exam, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `exam_${exam.slug || 'new'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // פונקציות לניהול סקטורים
  const toggleSector = (sector) => {
    setExam((prev) => ({
      ...prev,
      targetSectors: prev.targetSectors.includes(sector)
        ? prev.targetSectors.filter((s) => s !== sector)
        : [...prev.targetSectors, sector],
    }));
  };

  const selectAllSectors = () => {
    setExam((prev) => ({
      ...prev,
      targetSectors: [...SECTORS],
    }));
  };

  const clearAllSectors = () => {
    setExam((prev) => ({
      ...prev,
      targetSectors: [],
    }));
  };

  // פונקציה להוספת שאלה חדשה
  const addQuestion = () => {
    setExam({
      ...exam,
      questions: [
        ...(exam.questions || []),
        {
          question: "",
          options: [
            { label: "A", text: "" },
            { label: "B", text: "" },
            { label: "C", text: "" },
            { label: "D", text: "" },
          ],
          correctAnswer: "",
          explanation: "",
          points: 1,
        },
      ],
    });
  };

  // פונקציה למחיקת שאלה
  const removeQuestion = (index) => {
    setExam({
      ...exam,
      questions: exam.questions.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="page" dir="rtl">
        <h2>עורך מבחן</h2>
        <div>טוען...</div>
      </div>
    );
  }

  return (
    <div className="page" dir="rtl">
      <button className="btn" onClick={() => nav("/admin/exams")}>
        חזור לרשימת מבחנים
      </button>
      <button
        className="btn"
        style={{ marginRight: "10px" }}
        onClick={() => nav("/admin/users")}
      >
        ניהול משתמשים
      </button>

      <h2>{isNew ? "יצירת מבחן חדש" : "עריכת מבחן"}</h2>

      {error && (
        <div
          style={{
            background: "#ffebe9",
            color: "#86181d",
            padding: "10px 12px",
            borderRadius: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div className="card">
        <h3>פרטי המבחן</h3>
        
        <label>מזהה המבחן *</label>
        <input
          value={exam.slug}
          onChange={(e) => setExam({ ...exam, slug: e.target.value })}
          placeholder="math-exam-2024"
          disabled={!isNew}
        />

        <label>שם המבחן *</label>
        <input
          value={exam.title}
          onChange={(e) => setExam({ ...exam, title: e.target.value })}
          placeholder="מבחן במתמטיקה 2024"
        />

        <label>תיאור המבחן</label>
        <textarea
          value={exam.description}
          onChange={(e) => setExam({ ...exam, description: e.target.value })}
          placeholder="תיאור קצר של המבחן..."
          rows={3}
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
          <div>
            <label>ציון עובר (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={exam.passScore}
              onChange={(e) => setExam({ ...exam, passScore: +e.target.value })}
            />
          </div>
          
          <div>
            <label>הגבלת זמן (דקות)</label>
            <input
              type="number"
              min="1"
              value={exam.timeLimit || ""}
              onChange={(e) => setExam({ ...exam, timeLimit: e.target.value ? +e.target.value : null })}
              placeholder="ללא הגבלה"
            />
          </div>
          
          <div>
            <label>מספר ניסיונות מקסימלי</label>
            <input
              type="number"
              min="1"
              max="10"
              value={exam.maxAttempts}
              onChange={(e) => setExam({ ...exam, maxAttempts: +e.target.value })}
            />
          </div>
        </div>

        <h3>הגדרות דיווח</h3>
        
        <label>מייל ממונה (לקבלת דיווחים)</label>
        <input
          type="email"
          value={exam.supervisorEmail}
          onChange={(e) => setExam({ ...exam, supervisorEmail: e.target.value })}
          placeholder="supervisor@company.com"
        />

        <label>
          <input
            type="checkbox"
            checked={exam.sendReportOnCompletion}
            onChange={(e) => setExam({ ...exam, sendReportOnCompletion: e.target.checked })}
            style={{ marginLeft: "8px" }}
          />
          שלח דיווח אוטומטי בסיום כל בחינה
        </label>

        {/* בחירת סקטורים */}
        <h3>סקטורים מיועדים *</h3>
        <div style={{ marginBottom: "10px" }}>
          <button
            type="button"
            className="btn"
            onClick={selectAllSectors}
            style={{
              marginLeft: "8px",
              fontSize: "0.8rem",
              padding: "6px 12px",
            }}
          >
            בחר הכל
          </button>
          <button
            type="button"
            className="btn"
            onClick={clearAllSectors}
            style={{ fontSize: "0.8rem", padding: "6px 12px" }}
          >
            בטל הכל
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          {SECTORS.map((sector) => (
            <label
              key={sector}
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "8px",
                background: exam.targetSectors.includes(sector)
                  ? "var(--accent)"
                  : "transparent",
                color: exam.targetSectors.includes(sector)
                  ? "#0b1c2b"
                  : "inherit",
              }}
            >
              <input
                type="checkbox"
                checked={exam.targetSectors.includes(sector)}
                onChange={() => toggleSector(sector)}
                style={{ marginLeft: "8px" }}
              />
              {sector}
            </label>
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <input
            type="file"
            accept="application/json"
            onChange={(e) => e.target.files[0] && importJson(e.target.files[0])}
            style={{ marginBottom: 12 }}
          />
          <button
            className="btn"
            onClick={save}
            disabled={loading}
            style={{ marginLeft: 8 }}
          >
            {loading ? "שומר..." : "💾 שמירה"}
          </button>
          
          {!isNew && (
            <>
              <button
                className="btn"
                onClick={exportJson}
                style={{ marginLeft: 8 }}
              >
                📥 ייצא JSON
              </button>
              
              <button
                className="btn"
                onClick={toggleActive}
                disabled={loading}
                style={{
                  marginLeft: 8,
                  background: exam.isActive ? "#e74c3c" : "#27ae60"
                }}
              >
                {exam.isActive ? "השבת מבחן" : "הפעל מבחן"}
              </button>
            </>
          )}
        </div>
      </div>

      <h3>שאלות המבחן ({exam.questions.length})</h3>
      {exam.questions.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "var(--text)" }}>אין שאלות במבחן זה</p>
          <p style={{ color: "var(--text)", fontSize: "0.9rem" }}>
            הוסף שאלות כדי ליצור מבחן פעיל
          </p>
        </div>
      )}
      
      {exam.questions.map((question, idx) => (
        <ExamQuestionEditor
          key={idx}
          value={question}
          index={idx}
          onChange={(updatedQuestion) => {
            const questions = [...exam.questions];
            questions[idx] = updatedQuestion;
            setExam({ ...exam, questions });
          }}
          onRemove={() => removeQuestion(idx)}
        />
      ))}
      
      <button
        className="btn"
        onClick={addQuestion}
        style={{ 
          background: "var(--accent)",
          color: "#0b1c2b",
          fontSize: "1rem",
          padding: "12px 24px"
        }}
      >
        + הוסף שאלה
      </button>

      {exam.questions.length > 0 && (
        <div className="card" style={{ marginTop: "20px" }}>
          <h4 style={{ marginTop: 0 }}>סיכום המבחן</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
            <div>
              <strong>סה"כ שאלות:</strong> {exam.questions.length}
            </div>
            <div>
              <strong>זמן משוער:</strong> {exam.timeLimit ? `${exam.timeLimit} דקות` : "ללא הגבלה"}
            </div>
            <div>
              <strong>ציון עובר:</strong> {exam.passScore}%
            </div>
            <div>
              <strong>נקודות כולל:</strong> {exam.questions.reduce((sum, q) => sum + (q.points || 1), 0)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}